import React, { useState } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';

type Organization = { id: string; fantasyName: string };

interface OrganizationSelectProps {
  organizations: Organization[];
  selectedOrganization: string | null;
  onOrganizationChange: (orgId: string) => void;
  isLoading?: boolean;
}

export default function OrganizationSelect({
  organizations,
  selectedOrganization,
  onOrganizationChange,
  isLoading = false
}: OrganizationSelectProps) {
  const [inputValue, setInputValue] = useState('');
  const selectedOrg = organizations.find(org => org.id === selectedOrganization);

  const filterOptions = createFilterOptions<Organization>({
    matchFrom: 'any',
    stringify: (option) => option.fantasyName,
    limit: 50
  });

  const handleChange = (event: any, newValue: Organization | null) => {
    if (newValue) {
      onOrganizationChange(newValue.id);
    }
  };

  const handleInputChange = (event: React.SyntheticEvent, newInputValue: string, reason: string) => {
    if (reason !== 'reset') {
      setInputValue(newInputValue);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      const input = event.target as HTMLInputElement;
      const currentInputValue = input.value;
      
      if (currentInputValue) {
        const filteredOptions = filterOptions(organizations, { 
          inputValue: currentInputValue, 
          getOptionLabel: (option) => option.fantasyName 
        });
        
        if (filteredOptions.length > 0) {
          onOrganizationChange(filteredOptions[0].id);
          setInputValue('');
        }
      }
    }
  };

  return (
    <Autocomplete
      options={organizations}
      getOptionLabel={(option) => option.fantasyName}
      value={selectedOrg || null}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      loading={isLoading}
      loadingText="Carregando..."
      noOptionsText="Nenhuma organização encontrada"
      freeSolo={false}
      autoHighlight
      autoSelect
      clearOnBlur={false}
      selectOnFocus={true}
      filterOptions={filterOptions}
      disablePortal
      onKeyDown={handleKeyDown}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Organização"
          placeholder="Digite para buscar..."
          inputProps={{
            ...params.inputProps,
            autoComplete: 'off'
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
    />
  );
}
